import { Api } from "@/api";
import { ImageFile, WorkListAttachment } from "@/utils/types/types";
import axios from "axios";
import heic2any from "heic2any";
import Cookies from "js-cookie";

type UploadType = "cart" | "presigned" | "crop";
type UploadFileRequest = { file: File; type: UploadType };
type UploadFileResponse = {
  fileUrl: string;
  presigned: {
    url: string;
    /** Presigned upload headers */
    fields: Record<string, string>;
  };
};

/**
 * An upload has three phases and only ONE of them can report a real number.
 *
 *  - "preparing"  — HEIC/WEBP are re-encoded to JPEG in the browser before
 *                   anything is sent. Instant for JPEG/PNG/TIFF, real work for
 *                   a large phone HEIC. No measurable progress.
 *  - "uploading"  — bytes leaving the browser for S3. `percent` here is a
 *                   genuine measurement from XHR upload events, not an
 *                   animation. This is the long phase on a slow uplink and the
 *                   one that was previously silent.
 *  - "processing" — polling for the processed preview. The poll answer is
 *                   binary ("is it there yet?"), so there is NO percentage to
 *                   report and any bar drawn here would be fake. `elapsedMs`
 *                   is provided instead so the UI can show a ticking timer
 *                   against the MAX_IMAGE_WAIT_MS cap.
 *
 * 2026-07-26: added because /upload gave a bare spinner for the whole of
 * phases 2 and 3 — up to ~4 min of processing on top of however long the
 * transfer took. Nothing was erroring; customers and staff were abandoning a
 * working upload because nothing on screen moved.
 */
export type UploadPhase = "preparing" | "uploading" | "processing";
export type UploadProgress = {
  phase: UploadPhase;
  /** 0–100. Only a real measurement while phase === "uploading". */
  percent: number;
  /** Time spent in the processing poll. Only set while phase === "processing". */
  elapsedMs?: number;
};
export type UploadProgressHandler = (progress: UploadProgress) => void;

/**
 * Must equal the server's presigned-POST `content-length-range` ceiling — that
 * policy is built from UPLOAD_MIN/UPLOAD_MAX in `functions/presigned-upload.ts`
 * and `functions/presigned-upload-document.ts`. UPLOAD_MAX is 500000000, i.e.
 * decimal MB. It is NOT 500 * 1024 * 1024, which would be 24MB too permissive
 * and would let a file upload in full only to be rejected by S3 at the end.
 *
 * The two dropzones (Drop.tsx, ProfileUploads.tsx) already gate on the same
 * number via MAX_FILE_SIZE_MB. This is the central backstop for the call sites
 * that have no dropzone in front of them — LineItemForm's bare FileInput,
 * Cropper, and the cart preview uploads.
 */
export const UPLOAD_MAX_BYTES = 500000000;

function assertWithinUploadLimit(file: File) {
  if (file.size <= UPLOAD_MAX_BYTES) return;
  const sizeMb = Math.round(file.size / 1000000);
  throw new Error(
    `This file is ${sizeMb}MB — the limit is 500MB. Please re-export it at a smaller size and try again.`
  );
}

const HEIC_EXTENSION_REGEX = /\.(heic|heif)$/i;
const KEEP_ORIGINAL_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/tiff",
  "image/tif",
]);

function buildJpegFileName(fileName: string) {
  const baseName = fileName.replace(/\.[^/.]+$/, "");
  return `${baseName}.jpg`;
}

function isHeicOrHeifFile(file: File) {
  return (
    file.type.toLowerCase().includes("heic") ||
    file.type.toLowerCase().includes("heif") ||
    HEIC_EXTENSION_REGEX.test(file.name)
  );
}

async function normalizeHeicUpload(file: File): Promise<File> {
  if (!isHeicOrHeifFile(file)) return file;

  try {
    const converted = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.95,
    });
    const convertedBlob = Array.isArray(converted) ? converted[0] : converted;
    if (!(convertedBlob instanceof Blob)) return file;

    return new File([convertedBlob], buildJpegFileName(file.name), {
      type: "image/jpeg",
    });
  } catch (error) {
    console.error("[upload] heic-normalization-failed", error);
    return file;
  }
}

async function convertImageToJpeg(file: File): Promise<File> {
  try {
    const imageUrl = URL.createObjectURL(file);
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () =>
        reject(new Error("Failed to decode image for jpeg conversion."));
      img.src = imageUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;

    const context = canvas.getContext("2d");
    if (!context || canvas.width <= 0 || canvas.height <= 0) {
      URL.revokeObjectURL(imageUrl);
      return file;
    }

    context.drawImage(image, 0, 0);

    const convertedBlob = await new Promise<Blob | null>(resolve => {
      canvas.toBlob(resolve, "image/jpeg", 0.95);
    });

    URL.revokeObjectURL(imageUrl);
    if (!convertedBlob) return file;

    return new File([convertedBlob], buildJpegFileName(file.name), {
      type: "image/jpeg",
    });
  } catch (error) {
    console.error("[upload] jpeg-normalization-failed", error);
    return file;
  }
}

const SMS_PHOTO_MAX_EDGE = 1600;

// Like convertImageToJpeg, but fits the long side to SMS_PHOTO_MAX_EDGE first.
// Used only for text-message photos (see UploadsApi.uploadSmsPhoto).
async function downscaleToJpeg(file: File): Promise<File> {
  const imageUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Could not read that image."));
      img.src = imageUrl;
    });
    const w = image.naturalWidth || image.width;
    const h = image.naturalHeight || image.height;
    if (w <= 0 || h <= 0) throw new Error("Could not read that image.");
    const scale = Math.min(1, SMS_PHOTO_MAX_EDGE / Math.max(w, h));

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Could not read that image.");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>(resolve => {
      canvas.toBlob(resolve, "image/jpeg", 0.85);
    });
    if (!blob) throw new Error("Could not read that image.");
    return new File([blob], buildJpegFileName(file.name), {
      type: "image/jpeg",
    });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

async function normalizeUploadFile(file: File): Promise<File> {
  if (isHeicOrHeifFile(file)) return normalizeHeicUpload(file);
  if (KEEP_ORIGINAL_MIME_TYPES.has(file.type.toLowerCase())) return file;
  return convertImageToJpeg(file);
}

// S3 rejects presigned POSTs with an XML error body (e.g. EntityTooLarge when
// the file busts the UPLOAD_MAX policy). Translate that into a plain-English
// Error the UI can show, and keep the original for Sentry (2026-07-15, 1S fix).
function friendlyS3UploadError(error: unknown): Error {
  console.error("[upload] s3-post-failed", error);
  (window as any).Sentry?.captureException(error);

  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const body =
    axios.isAxiosError(error) && typeof error.response?.data === "string"
      ? error.response.data
      : "";

  if (status === 400) {
    if (
      body.includes("EntityTooLarge") ||
      body.includes("exceeds the maximum")
    ) {
      return new Error("This file is too large to upload.");
    }
    return new Error(
      "This file type isn't supported — please use JPEG, PNG, TIFF, WEBP or HEIC."
    );
  }
  return new Error("Upload failed — please try again.");
}

export const UploadsApi = {
  async uploadFile(
    payload: UploadFileRequest,
    options?: { targetUserId?: string; onProgress?: UploadProgressHandler }
  ) {
    const onProgress = options?.onProgress;
    const UPLOAD_TYPE_URL: Record<UploadType, string> = {
      presigned: "presigned-upload",
      cart: "presigned-upload-cart",
      crop: "presigned-upload-crop",
    };
    const url = UPLOAD_TYPE_URL[payload.type];

    onProgress?.({ phase: "preparing", percent: 0 });
    const file = await normalizeUploadFile(payload.file);

    // Checked AFTER normalization, deliberately: HEIC and WEBP are re-encoded
    // to JPEG above, so the bytes S3 measures against its policy are not the
    // bytes the user picked. TIFF/JPEG/PNG pass through untouched, so for the
    // large-file case that actually matters this still fails instantly.
    assertWithinUploadLimit(file);

    const res = await Api.post<UploadFileResponse>(url, {
      fileName: file.name,
      fileType: file.type,
      guest_user_id: Cookies.get("guest_user_id"),
      stytch_user_id: Cookies.get("stytch_user_id"),
      // Admin-only: upload into another customer's account. The server
      // verifies the caller is an admin before honoring this.
      target_user_id: options?.targetUserId || undefined,
    });

    const form = new FormData();
    Object.entries(res.data.presigned.fields).forEach(([k, v]) =>
      form.append(k, v)
    );
    form.append("file", file);

    onProgress?.({ phase: "uploading", percent: 0 });
    try {
      await axios.post(res.data.presigned.url, form, {
        onUploadProgress: event => {
          if (!onProgress || !event.total) return;
          // event.total covers the whole multipart body, so it includes the
          // presigned fields as well as the file. Those are a few hundred
          // bytes against a file measured in MB — not worth correcting for.
          const percent = Math.min(
            100,
            Math.round((event.loaded / event.total) * 100)
          );
          onProgress({ phase: "uploading", percent });
        },
      });
    } catch (error) {
      throw friendlyS3UploadError(error);
    }

    return res;
  },
  async uploadFileToS3(
    file: File,
    options?: { targetUserId?: string; onProgress?: UploadProgressHandler }
  ) {
    const onProgress = options?.onProgress;
    const { data } = await UploadsApi.uploadFile(
      { file, type: "presigned" },
      options
    );

    // F7: the processed preview poll used to recurse FOREVER — any file the
    // image processor choked on produced an endless spinner (live Sentry 1S).
    // Cap the wait and surface a friendly, actionable error instead.
    const MAX_IMAGE_WAIT_MS = 4 * 60 * 1000;
    const POLL_INTERVAL_MS = 2000;

    async function waitForImage(startedAt: number): Promise<string> {
      // No percentage exists for this phase — see UploadProgress above. Report
      // elapsed time so the UI can prove it is still alive.
      onProgress?.({
        phase: "processing",
        percent: 100,
        elapsedMs: Date.now() - startedAt,
      });
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      try {
        await axios.head(data.fileUrl);
        console.info("Image ready!");
        return data.fileUrl;
      } catch {
        if (Date.now() - startedAt >= MAX_IMAGE_WAIT_MS) {
          const timeoutError = new Error(
            "We couldn't finish processing this file. If it's a TIFF, try re-exporting it as a flattened TIFF (no layers or extra channels) or a JPEG — or contact the studio and we'll take care of it."
          );
          (window as any).Sentry?.captureException(timeoutError, {
            extra: { fileUrl: data.fileUrl, fileName: file.name },
          });
          throw timeoutError;
        }
        console.info("Waiting for image.");
        return waitForImage(startedAt);
      }
    }

    const fileUrl = await waitForImage(Date.now());

    await Api.post("/register-upload", {
      fileUrl,
      stytch_user_id: Cookies.get("stytch_user_id"),
      guest_user_id: Cookies.get("guest_user_id"),
      target_user_id: options?.targetUserId || undefined,
    }).catch(err => {
      console.error("[upload] register-upload failed", err);
    });

    return fileUrl;
  },
  // Uploads an arbitrary document (PDF/Office/etc.) for a worklist. Unlike
  // uploadFileToS3, the file is sent to S3 untouched (no JPEG re-encode) and is
  // not registered in the image Upload table.
  async uploadDocumentToS3(
    file: File,
    orderNo?: string,
    options?: { onProgress?: UploadProgressHandler }
  ): Promise<WorkListAttachment> {
    const onProgress = options?.onProgress;

    // Documents are sent untouched (no re-encode), so the file the caller
    // hands us is exactly what S3 measures.
    assertWithinUploadLimit(file);

    const res = await Api.post<{
      fileUrl: string;
      key: string;
      presigned: { url: string; fields: Record<string, string> };
    }>("/presigned-upload-document", {
      fileName: file.name,
      fileType: file.type || "application/octet-stream",
      orderNo,
      stytch_user_id: Cookies.get("stytch_user_id"),
    });

    const form = new FormData();
    Object.entries(res.data.presigned.fields).forEach(([k, v]) =>
      form.append(k, v)
    );
    form.append("file", file);

    onProgress?.({ phase: "uploading", percent: 0 });
    try {
      await axios.post(res.data.presigned.url, form, {
        onUploadProgress: event => {
          if (!onProgress || !event.total) return;
          const percent = Math.min(
            100,
            Math.round((event.loaded / event.total) * 100)
          );
          onProgress({ phase: "uploading", percent });
        },
      });
    } catch (error) {
      throw friendlyS3UploadError(error);
    }

    // F7: same infinite-poll guard as image uploads (documents skip the
    // image processor, so this should resolve quickly).
    const DOC_WAIT_START = Date.now();
    async function waitForFile(): Promise<void> {
      await new Promise(resolve => setTimeout(resolve, 2000));
      try {
        await axios.head(res.data.fileUrl);
      } catch {
        if (Date.now() - DOC_WAIT_START >= 2 * 60 * 1000) {
          throw new Error(
            "This document upload didn't finish — please try again."
          );
        }
        return waitForFile();
      }
    }
    await waitForFile();

    return {
      url: res.data.fileUrl,
      key: res.data.key,
      name: file.name,
      size: file.size,
      contentType: file.type || undefined,
      uploadedAt: new Date().toISOString(),
    };
  },
  // Photo attached to an outbound text (MMS). Reference quality only — never a
  // print file: HEIC → JPEG, downscaled to SMS_PHOTO_MAX_EDGE on the long side
  // and re-encoded, so the result sits well under Twilio's 5MB MMS cap (a phone
  // photo lands around 200–500KB). Stored via the document endpoint (untouched
  // by the image processor, served straight from the CDN) under the fixed
  // "sms" scope, which is the public URL Twilio fetches.
  async uploadSmsPhoto(file: File): Promise<string> {
    const jpeg = await downscaleToJpeg(await normalizeHeicUpload(file));
    const doc = await this.uploadDocumentToS3(jpeg, "sms");
    return doc.url;
  },
  // `force` bypasses the server's cart-reference guard (409 UPLOAD_IN_CART)
  // after the user has confirmed. See utils/deleteUploadGuarded.ts.
  deleteFileByUrl(url: string, options?: { force?: boolean }) {
    const { pathname } = new URL(url);
    return Api.post<void>("/delete-s3-upload", {
      fileKey: pathname.slice(1),
      ...(options?.force ? { force: true } : {}),
    });
  },
  hideFileByUrl(url: string) {
    const { pathname } = new URL(url);
    return Api.post<void>("/hide-upload", {
      fileKey: pathname.slice(1),
    });
  },
  unhideFileByUrl(url: string) {
    const { pathname } = new URL(url);
    return Api.post<void>("/unhide-upload", {
      fileKey: pathname.slice(1),
    });
  },
  getUploads(page = 1, pageSize = 500) {
    return Api.post<ImageFile[]>("/recent-uploads", {
      guest_user_id: Cookies.get("guest_user_id"),
      stytch_user_id: Cookies.get("stytch_user_id"),
      page,
      pageSize,
    });
  },
  getUploadsIncludingHidden(page = 1, pageSize = 500) {
    return Api.post<ImageFile[]>("/recent-uploads", {
      guest_user_id: Cookies.get("guest_user_id"),
      stytch_user_id: Cookies.get("stytch_user_id"),
      page,
      pageSize,
      includeHidden: true,
    });
  },
  // Admin method — must include the admin's stytch_user_id so the server
  // passes its auth check before using user_id as the target.
  getUploadsByUserId(userId: string) {
    return Api.post<ImageFile[]>("/recent-uploads", {
      stytch_user_id: Cookies.get("stytch_user_id"),
      user_id: userId,
      includeHidden: true,
    });
  },
  // Admin method — copy an existing upload (any account) into a customer's
  // account. S3 variants are copied under a fresh hash; the source is left
  // untouched. Server verifies the caller is an admin.
  copyUploadToUser(sourceUrl: string, targetUserId: string) {
    return Api.post<ImageFile>("/copy-upload", {
      stytch_user_id: Cookies.get("stytch_user_id"),
      source_url: sourceUrl,
      target_user_id: targetUserId,
    });
  },
  // Admin method — fetches all uploads across all accounts with pagination.
  getAllUploads(page = 1, pageSize = 100, search?: string) {
    return Api.post<{
      uploads: (ImageFile & {
        webUID?: string;
        webGUID?: string;
        userFirstName?: string;
        userLastName?: string;
        userEmail?: string | null;
      })[];
      total: number;
      page: number;
      pageSize: number;
    }>("/all-uploads", { page, pageSize, search });
  },
};
